/**
 * Confetti rains down over the whole window: one of the party popper's
 * emotes (see emotes.ts, which shows who sent it), and a rave's drops. One
 * canvas is shared, so confetti that arrives while some is still falling adds
 * to the storm.
 */

const COLORS = ["#f2c446", "#e8604c", "#3f9a6b", "#4a7fd6", "#b06fd8", "#f08fb0", "#2fb3b0"];

interface Piece {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  shape: "rect" | "circle" | "ribbon";
  angle: number;
  spin: number;
  /** Phase of the side-to-side flutter and the 3D flip. */
  wobble: number;
  wobbleSpeed: number;
}

let canvas: HTMLCanvasElement | null = null;
let pieces: Piece[] = [];
let toSpawn = 0;
/** Fractional pieces owed to the next frame, so the rain stays even at any frame rate. */
let carry = 0;
let frame = 0;
let last = 0;
/** Everyone waiting for the confetti to finish falling. */
const waiting: Array<() => void> = [];

function resize() {
  if (!canvas) return;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = innerWidth * dpr;
  canvas.height = innerHeight * dpr;
  canvas.getContext("2d")!.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function spawn(): Piece {
  const r = Math.random();
  return {
    x: Math.random() * innerWidth,
    y: -20 - Math.random() * 40,
    vx: (Math.random() - 0.5) * 1.6,
    vy: 1.5 + Math.random() * 2.5,
    size: 6 + Math.random() * 6,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    shape: r < 0.6 ? "rect" : r < 0.8 ? "circle" : "ribbon",
    angle: Math.random() * Math.PI * 2,
    spin: (Math.random() - 0.5) * 0.25,
    wobble: Math.random() * Math.PI * 2,
    wobbleSpeed: 0.05 + Math.random() * 0.08,
  };
}

function draw(ctx: CanvasRenderingContext2D, p: Piece) {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.angle);
  // Squash vertically as it tumbles, so flat pieces look like they're flipping over.
  ctx.scale(1, Math.cos(p.wobble * 1.3));
  ctx.fillStyle = p.color;
  if (p.shape === "circle") {
    ctx.beginPath();
    ctx.arc(0, 0, p.size * 0.38, 0, Math.PI * 2);
    ctx.fill();
  } else if (p.shape === "ribbon") {
    ctx.fillRect(-p.size * 0.15, -p.size, p.size * 0.3, p.size * 2);
  } else {
    ctx.fillRect(-p.size / 2, -p.size * 0.3, p.size, p.size * 0.6);
  }
  ctx.restore();
}

function tick(now: number) {
  if (!canvas) return;
  // Frame-rate independent: 1 = one 60 Hz frame. Capped so a backgrounded tab doesn't teleport everything.
  const dt = Math.min(3, (now - last) / 16.67 || 1);
  last = now;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, innerWidth, innerHeight);

  // About 110 pieces a second, so a press rains for a couple of seconds rather than dropping in one clump.
  carry = toSpawn > 0 ? carry + 1.8 * dt : 0;
  const batch = Math.min(toSpawn, Math.floor(carry));
  carry -= batch;
  for (let i = 0; i < batch; i++) pieces.push(spawn());
  toSpawn -= batch;

  for (const p of pieces) {
    p.wobble += p.wobbleSpeed * dt;
    p.vy = Math.min(p.vy + 0.05 * dt, 4.5);
    p.vx *= 0.99;
    p.x += (p.vx + Math.sin(p.wobble) * 0.9) * dt;
    p.y += p.vy * dt;
    p.angle += p.spin * dt;
    draw(ctx, p);
  }
  pieces = pieces.filter((p) => p.y < innerHeight + 30);

  if (pieces.length || toSpawn > 0) frame = requestAnimationFrame(tick);
  else stop();
}

function stop() {
  cancelAnimationFrame(frame);
  window.removeEventListener("resize", resize);
  canvas?.remove();
  canvas = null;
  pieces = [];
  toSpawn = 0;
  carry = 0;
  for (const done of waiting.splice(0)) done();
}

/** Rain confetti, resolving once all of it, including any that's added meanwhile, has fallen. */
export function rainConfetti(pieces: number): Promise<void> {
  const calm = matchMedia("(prefers-reduced-motion: reduce)").matches;
  toSpawn += calm ? Math.min(pieces, 60) : pieces;
  const done = new Promise<void>((resolve) => waiting.push(resolve));
  if (canvas) return done;
  canvas = document.createElement("canvas");
  canvas.className = "confetti-canvas";
  canvas.setAttribute("aria-hidden", "true");
  document.body.appendChild(canvas);
  resize();
  window.addEventListener("resize", resize);
  last = performance.now();
  frame = requestAnimationFrame(tick);
  return done;
}
